import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('nx_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('nx_token');
      localStorage.removeItem('nx_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Backend liveness + a small set of safe, boolean-only readiness signals
// (never a raw secret/config value) -- consumed by RealBrandReadinessPage.js's
// own live CORS check.
export const healthAPI = {
  check: () => api.get('/health'),
};

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  // AF-3.17.1 Section 4: revokes exactly this token server-side (a
  // per-token "jti" denylist entry) -- logout is no longer purely
  // client-side localStorage clearing.
  logout: () => api.post('/auth/logout'),
  // Free Evaluation Workspace -- honest self-serve unlock, not a fake
  // paywall (no billing system exists to gate behind).
  activateFullWorkspace: () => api.post('/auth/activate-full-workspace'),
  setProductAuditAccess: (tenantId, scopes) => api.post('/auth/product-audit-access', { tenant_id: tenantId, scopes }),
};

// Milestone 7A (Unified Intelligence Workspace) -- Company Context. GET
// creates the (honestly empty, save for one real inferred field) doc on
// first read; PUT is the brand's real add/edit/delete surface -- a
// field sent as "" or [] is a real, intentional delete server-side.
export const companyContextAPI = {
  get: (clientId) => api.get(`/company-context/${clientId}`),
  update: (clientId, fields) => api.put(`/company-context/${clientId}`, fields),
};

// Website -> Company Context discovery (Step 3 of the brand journey).
// One call does the whole "website only in" step server-side: creates
// (or reuses) the client, researches the site, and returns the
// already-populated Company Context -- see server.py's
// start_brand_discovery / company_discovery.py.
export const brandDiscoveryAPI = {
  start: (website) => api.post('/brand/discovery/start', { website }),
};

// Free Evaluation Workspace's "Your Sahib Briefing" data source.
// Milestone 6.1 -- both methods take an optional clientId, additive:
// a normal brand caller never passes one (unchanged behavior, picks
// "my own client" server-side exactly as before); an explicitly-entered
// admin workspace context passes the real, owned client_id it verified
// via POST /workspace-context/enter.
export const briefingAPI = {
  get: (clientId) => api.get('/brand/briefing', { params: clientId ? { client_id: clientId } : {} }),
  // Milestone 1 (AI CGO Wedge) -- lightweight polling companion to
  // get(): tells the frontend whether Sahib's first check is still
  // running, without re-running VisibilityService/DiagnosticEngine/
  // OpportunityEngine on every poll tick.
  status: (clientId) => api.get('/brand/briefing/status', { params: clientId ? { client_id: clientId } : {} }),
  // Milestone 42 (AI CGO Workspace: Judgment-First Executive Briefing) --
  // "What Sahib Found"'s real per-domain data source: a thin read-only
  // wrapper around sahib_intelligence_bridge.build_cross_domain_snapshot(),
  // the SAME real aggregator Talk to Sahib already uses.
  domainFindings: (clientId) => api.get(`/brand/${clientId}/domain-findings`),
};

// Milestone 6.1 (Explicit Workspace Context) -- the one new route: an
// admin deliberately entering a workspace it already owns. Never a
// token swap -- the caller's own auth is unchanged before and after.
export const workspaceContextAPI = {
  enter: (clientId) => api.post('/workspace-context/enter', { client_id: clientId }),
};

// Milestone 3 (AI CGO Wedge) -- real prepared work (prepared_work.py's
// PreparedWorkService), grounded in the same real findings GET
// /brand/briefing already shows. updateStatus is the ONLY way a
// status ever changes -- Sahib itself never calls it; every transition
// is a human action from the AI CGO Home.
export const preparedWorkAPI = {
  list: (clientId) => api.get(`/prepared-work/${clientId}`),
  updateStatus: (clientId, workId, status) => api.post(`/prepared-work/${clientId}/${workId}/status`, { status }),
  // Phase 1 (Work -> Edit): the founder's own edits to the brief text.
  editOutput: (clientId, workId, fields) => api.post(`/prepared-work/${clientId}/${workId}/edit`, { fields }),
  // Real, in-place retry for the one honest failure mode a prepared-work
  // item can be stuck in: a real LLM call that failed (e.g. the
  // provider account had no credits). Never creates a duplicate item --
  // updates the SAME id server-side (prepared_work.py's
  // retry_generation).
  retryGeneration: (clientId, workId) => api.post(`/prepared-work/${clientId}/${workId}/retry-generation`),
  // Milestone 9 (Paid Growth Handoff) -- a second, explicitly human-
  // triggered generation step, only ever available once approved
  // (prepared_work.py's generate_handoff). Kept as a method on this
  // same preparedWorkAPI object, matching retryGeneration right above,
  // rather than a separate handoffAPI export -- one real API surface
  // for this one primitive, not a new abstraction.
  generateHandoff: (clientId, workId) => api.post(`/prepared-work/${clientId}/${workId}/handoff`),
  // Milestone 11 (Paid Growth Current-State Comparison) -- GET, not POST
  // like the three methods above: this is the one prepared-work
  // sub-action that writes nothing server-side (server.py's
  // compare_prepared_work_lineage is a plain read/compute over the
  // item's own real historical lineage + a fresh live detection).
  compareLineage: (clientId, workId) => api.get(`/prepared-work/${clientId}/${workId}/compare`),
};

// Milestone 23 (ChatGPT Ads -- AI CGO Execution Loop) -- Talk to Sahib's
// first real backend call. Never itself executes anything (server.py's
// talk_to_sahib_message route only ever reaches a real mutation via
// chatgptAdsAPI.approvePlan below, gated on a plan already at
// ready_for_approval for this exact threadId).
export const talkToSahibAPI = {
  sendMessage: (clientId, threadId, text, context) => api.post(`/talk-to-sahib/${clientId}/message`, { thread_id: threadId, text, context }),
};

// Milestone 23 -- the campaign-plan state machine's own explicit routes.
// approvePlan is the ONE call in this file that can result in a real
// external ad-spend mutation -- always requires the human to have
// already seen the plan this exact plan_id refers to.
export const chatgptAdsAPI = {
  getPlan: (clientId, planId) => api.get(`/chatgpt-ads/${clientId}/plans/${planId}`),
  approvePlan: (clientId, planId) => api.post(`/chatgpt-ads/${clientId}/plans/${planId}/approve`),
  cancelPlan: (clientId, planId) => api.post(`/chatgpt-ads/${clientId}/plans/${planId}/cancel`),
  // Milestone 28 -- mirrors the three calls above exactly, for the
  // SEPARATE CampaignChangePlan state machine (pause/resume/budget
  // updates, and recommendations that produce one). Closes a real gap:
  // since Milestone 25, these mutations have been chat-text-only
  // ("approve"/"cancel"), with no button-driven route at all.
  getChangePlan: (clientId, planId) => api.get(`/chatgpt-ads/${clientId}/change-plans/${planId}`),
  approveChangePlan: (clientId, planId) => api.post(`/chatgpt-ads/${clientId}/change-plans/${planId}/approve`),
  cancelChangePlan: (clientId, planId) => api.post(`/chatgpt-ads/${clientId}/change-plans/${planId}/cancel`),
};

// Hero cleanup -- the public "Apply for Early Access" CTA's only call.
export const earlyAccessAPI = {
  apply: (data) => api.post('/early-access/apply', data),
};

// Clients
export const clientsAPI = {
  list: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// SEO
export const seoAPI = {
  audits: (clientId) => api.get(`/seo/audits/${clientId}`),
  keywords: (clientId) => api.get(`/seo/keywords/${clientId}`),
  brokenLinks: (clientId) => api.get(`/seo/broken-links/${clientId}`),
  runAudit: (clientId) => api.post(`/seo/audits/${clientId}/run`),
};

// GEO
// Unique key for one deliberate, confirmed paid operation. The backend
// persists this key's fingerprint/result; retries must reuse the same value.
export function createIdempotencyKey() {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(24);
    c.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // No WebCrypto: refuse rather than fall back to a guessable key. Without a
  // key no paid request is sent (the backend would answer 428 anyway).
  throw new Error('A secure random generator is required to start a paid GEO run.');
}

export const geoAPI = {
  optimization: (clientId) => api.get(`/geo/optimization/${clientId}`),
  history: (clientId) => api.get(`/geo/history/${clientId}`),
  // Idempotency-Key is generated once per confirmed operation and reused for
  // retries so a lost response cannot charge or execute the same run twice.
  runCheck: (clientId, idempotencyKey) => api.post(`/geo/run-check/${clientId}`, null, { headers: { 'Idempotency-Key': idempotencyKey } }),
  runCheckEstimate: (clientId) => api.get(`/geo/run-check/${clientId}/estimate`),
  runAllEstimate: () => api.get('/geo/run-all/estimate'),
  report: () => api.get('/geo/report'),
  runAll: (idempotencyKey) => api.post('/geo/run-all', null, { headers: { 'Idempotency-Key': idempotencyKey } }),
};

// Ads
export const adsAPI = {
  campaigns: (clientId) => api.get(`/ads/campaigns/${clientId}`),
  createCampaign: (data) => api.post('/ads/campaigns', data),
  updateCampaign: (id, data) => api.put(`/ads/campaigns/${id}`, data),
  performance: (clientId) => api.get(`/ads/performance/${clientId}`),
  skuReport: (clientId) => api.get(`/ads/sku-report/${clientId}`),
  // Meta Andromeda
  andromedaAudit: (clientId) => api.get(`/ads/meta/andromeda-audit/${clientId}`),
  creativePortfolio: (clientId) => api.get(`/ads/meta/creative-portfolio/${clientId}`),
  aiCitationOverlay: (clientId) => api.get(`/ads/meta/ai-citation-overlay/${clientId}`),
  metaApprovals: (clientId) => api.get(`/ads/meta/approvals/${clientId}`),
  // Google Ads Intelligence
  pmaxAudit: (clientId) => api.get(`/google-ads/pmax-audit/${clientId}`),
  assetGroups: (clientId) => api.get(`/google-ads/asset-groups/${clientId}`),
  shoppingFeed: (clientId) => api.get(`/google-ads/shopping-feed/${clientId}`),
  aiMaxSearch: (clientId) => api.get(`/google-ads/ai-max/${clientId}`),
  googleApprovals: (clientId) => api.get(`/google-ads/approvals/${clientId}`),
  qsSomCorrelation: (clientId) => api.get(`/google-ads/qs-som-correlation/${clientId}`),
};

// CMS
export const cmsAPI = {
  pages: (clientId) => api.get(`/cms/pages/${clientId}`),
  createPage: (data) => api.post('/cms/pages', data),
  updatePage: (id, data) => api.put(`/cms/pages/${id}`, data),
  deletePage: (id) => api.delete(`/cms/pages/${id}`),
  maintenance: (clientId) => api.get(`/cms/maintenance/${clientId}`),
};

// Agents
export const agentsAPI = {
  activity: () => api.get('/agents/activity'),
  // Milestone 1 (AI CGO Wedge) -- for the AI CGO Home's "Agents at work"
  // region. Unlike activity() above, never falls back to fabricated
  // activity: an empty list here is real and must be rendered honestly.
  recentActivity: (params) => api.get('/agents/activity/recent', { params }),
  trigger: (data) => api.post('/agents/trigger', data),
  // Schedules
  schedules: () => api.get('/agents/schedules'),
  createSchedule: (data) => api.post('/agents/schedules', data),
  updateSchedule: (id, data) => api.put(`/agents/schedules/${id}`, data),
  deleteSchedule: (id) => api.delete(`/agents/schedules/${id}`),
  // Recommendations
  recommendations: (status) => api.get('/agents/recommendations', { params: status ? { status } : {} }),
  pendingCount: () => api.get('/agents/recommendations/pending-count'),
  actionRecommendation: (id, action) => api.put(`/agents/recommendations/${id}`, { action }),
  bulkAction: (action) => api.post(`/agents/recommendations/bulk-action?action=${action}`),
  // Runs
  runs: () => api.get('/agents/runs'),
  runScheduled: (clientId) => api.post(`/agents/run-scheduled/${clientId}`),
};

// Dashboard
export const dashboardAPI = {
  overview: () => api.get('/dashboard/overview'),
};

// Tavily Search
export const searchAPI = {
  web: (query, searchDepth = 'advanced', maxResults = 5) =>
    api.post('/search/web', { query, search_depth: searchDepth, max_results: maxResults }),
  competitor: (domain, keywords = []) =>
    api.post('/search/competitor', { domain, keywords }),
  // Paid (Tavily): called only after confirmation. The same idempotency key
  // must be reused if the caller retries the same operation.
  geoCheck: (clientId, prompts = [], idempotencyKey) =>
    api.post('/search/geo-check', { client_id: clientId, prompts }, { headers: { 'Idempotency-Key': idempotencyKey } }),
  // Read-only estimate of exactly what geoCheck would run and charge.
  geoCheckEstimate: (clientId, prompts = []) =>
    api.get('/search/geo-check/estimate', {
      params: new URLSearchParams([['client_id', clientId], ...prompts.map((p) => ['prompts', p])]),
    }),
  status: () => api.get('/search/status'),
  usage: () => api.get('/tavily/usage'),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Notifications
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Team
export const teamAPI = {
  list: () => api.get('/team'),
  invite: (data) => api.post('/team/invite', data),
  updateRole: (id, role) => api.put(`/team/${id}/role`, { role }),
  remove: (id) => api.delete(`/team/${id}`),
};

// GEO Playbook
export const playbookAPI = {
  generate: (clientId) => api.post(`/geo/playbook/${clientId}`),
  list: (clientId) => api.get(`/geo/playbooks/${clientId}`),
};

// Cron
export const cronAPI = {
  scheduleStatus: () => api.get('/cron/schedule-status'),
  dailyAudits: () => api.post('/cron/daily-audits'),
  weeklyGeo: () => api.post('/cron/weekly-geo'),
};

// Vault
export const vaultAPI = {
  save: (data) => api.post('/vault/save', data),
  keys: (clientId) => api.get('/vault/keys', { params: clientId ? { client_id: clientId } : {} }),
  validate: (keyName, clientId) => api.post(`/vault/validate/${keyName}`, null, { params: clientId ? { client_id: clientId } : {} }),
  delete: (keyName, clientId) => api.delete(`/vault/keys/${keyName}`, { params: clientId ? { client_id: clientId } : {} }),
  availableKeys: () => api.get('/vault/available-keys'),
};

// Master
export const masterAPI = {
  overview: () => api.get('/master/overview'),
  auditLog: () => api.get('/master/audit-log'),
};

// Credits
export const creditsAPI = {
  usage: () => api.get('/credits/usage'),
};

// Creative (soft launch: brief -> one image -> download -> usage). Image bytes
// are behind bearer auth, so <img src> cannot be used -- see AuthImage.
export const creativeAPI = {
  usage: () => api.get('/creative/usage'),
  list: (clientId) => api.get(`/creative/list/${clientId}`),
  generate: (payload) => api.post('/creative/generate', payload),
  imageBlob: (imageId) => api.get(`/creative/image/${imageId}`, { responseType: 'blob' }),
  brandKit: (clientId) => api.get(`/creative/brand-kit/${clientId}`),
  saveBrandKit: (payload) => api.post('/creative/brand-kit', payload),
};

// Attribution
export const attributionAPI = {
  setup: (clientId) => api.get(`/attribution/setup/${clientId}`),
};

// Search Everywhere — explicit competitive prompt execution (Stage 3.42 bridge).
// Distinct from SoM's somPromptsAPI.runNow, which never loads db.competitors.
export const searchEverywhereAPI = {
  executeCompetitive: (promptId, params = {}) => api.post(`/v2/prompts/${promptId}/execute-competitive`, null, { params }),
};

// v2 Search Everywhere Intelligence
export const intelligenceAPI = {
  overview: (clientId, params) => api.get(`/v2/intelligence/${clientId}/overview`, { params }),
  trends: (clientId, params) => api.get(`/v2/intelligence/${clientId}/trends`, { params }),
  diagnostics: (clientId) => api.get(`/v2/intelligence/${clientId}/diagnostics`),
  opportunities: (clientId) => api.get(`/v2/intelligence/${clientId}/opportunities`),
  sources: (clientId) => api.get(`/v2/intelligence/${clientId}/sources`),
  competitors: (clientId) => api.get(`/v2/intelligence/${clientId}/competitors`),
  changes: (clientId, params) => api.get(`/v2/intelligence/${clientId}/changes`, { params }),
  recommendations: (clientId) => api.get(`/v2/intelligence/${clientId}/recommendations`),
  prompts: (clientId, promptType = null) => api.get(`/v2/prompts/${clientId}`, { params: promptType ? { prompt_type: promptType } : undefined }),
  promptHistory: (promptId) => api.get(`/v2/prompts/${promptId}/history`),
  actions: (clientId) => api.get(`/v2/optimization-actions/${clientId}`),
  actionImpact: (actionId) => api.get(`/v2/optimization-actions/${actionId}/impact`),
  usage: () => api.get('/v2/usage'),
  usageRemaining: () => api.get('/v2/usage/remaining'),
};

// Raw AI Data console (AIRawDataPage.js) -- both routes already existed,
// real, tenant-scoped, and complete (server.py's v2_execution_history /
// v2_citation_history) with zero frontend consumer until now. No new
// backend behavior; params map 1:1 onto each route's own optional query
// params (confirmed by direct read, not assumed).
export const aiResponsesAPI = {
  list: (clientId, params = {}) => api.get(`/v2/executions/${clientId}`, { params }),
};
export const citationsAPI = {
  list: (clientId, params = {}) => api.get(`/v2/citations/${clientId}/history`, { params }),
};

// DataForSEO real search-data sync (SearchDataPage.js) -- Milestone 52's
// own real, live-call-capable routes. DataForSEO is a shared,
// platform-level infrastructure credential (server.py's own
// INFRASTRUCTURE_KEYS / DATAFORSEO_API_KEY, resolved the same way for
// every tenant), not a per-tenant OAuth or pasted-secret connection like
// Shopify/Meta/Google Ads -- so there is no per-tenant "Connect" step to
// reuse from DataConnectionsPage.js (verified by direct read:
// DataConnectionsPage.js's own ConnectDialog never lists "dataforseo" as
// a provider, and connectorsAPI's db.connectors documents are a
// structurally different, per-tenant-credential concept this provider
// deliberately does not use -- see connectors/dataforseo.py's own module
// docstring). `status()` reports whether the shared platform key is
// configured; `syncKeyword` triggers ONE real, live organic-SERP ranking
// check for a real keyword against this client's own real website,
// persisting a real intelligence_measurements record through the
// existing connector -> intelligence_bridge pipeline -- never a mock,
// never a second sync mechanism.
export const dataforseoAPI = {
  status: () => api.get('/connections/dataforseo/status'),
  syncKeyword: (clientId, { keyword, location_code, language_code } = {}) =>
    api.post(`/connections/dataforseo/sync/${clientId}`, null, {
      params: { keyword, location_code, language_code },
    }),
};

// Competitor configuration — the source collection behind graph sync (Stage 3.41)
// and Search Everywhere's derived competitor intelligence (intelligenceAPI.competitors).
export const competitorsAPI = {
  list: (clientId) => api.get(`/v2/competitors/${clientId}`),
  create: (clientId, payload) => api.post('/v2/competitors', null, {
    params: {
      client_id: clientId,
      brand_name: payload.brand_name,
      domain: payload.domain,
      competitor_type: payload.competitor_type,
    },
  }),
  remove: (competitorId) => api.delete(`/v2/competitors/${competitorId}`),
  syncGraph: (clientId) => api.post(`/v3/graph/clients/${clientId}/sync-competitors`),
};

// Phase 0 (P0.4) -- Martian suggests competitors from this workspace's own
// AI answers; nothing reaches the competitor list until the founder approves.
export const competitorSuggestionsAPI = {
  list: (clientId, { refresh = false } = {}) => api.get(`/v2/competitor-suggestions/${clientId}`, { params: { refresh } }),
  approve: (clientId, suggestionId) => api.post(`/v2/competitor-suggestions/${clientId}/${suggestionId}/approve`),
  reject: (clientId, suggestionId) => api.post(`/v2/competitor-suggestions/${clientId}/${suggestionId}/reject`),
};

// SoM Prompt Creator — configuration plus custom Run Now (not production SoM)
// Phase 0 (P0.7) -- the founder approves which questions Martian watches;
// recurring monitoring only runs for an approved watchlist.
export const watchlistAPI = {
  decide: (clientId, promptIds, approved = true) =>
    api.post(`/v2/watchlist/${clientId}/decision`, { prompt_ids: promptIds, approved }),
  // "Let Martian suggest": rebuild Martian's own questions from the current
  // Company Context -- configuration only, no provider call, no credits.
  // Deliberately NOT on somPromptsAPI (the Prompt Creator never rebuilds).
  suggest: (clientId) => api.post(`/v2/som-prompts/${clientId}/ensure`),
  // "Import from Google Search Console": real queries from this workspace's
  // existing GSC connection, previewed as candidates (nothing saved), then
  // imported by id as SUGGESTED questions -- never approved, never
  // monitored until the founder approves them with `decide`. The server
  // re-reads Search Console on import; no question text, metric or
  // credential is ever sent from here.
  gscCandidates: (clientId) => api.get(`/v2/watchlist/${clientId}/gsc-candidates`),
  gscImport: (clientId, candidateIds) =>
    api.post(`/v2/watchlist/${clientId}/gsc-import`, { candidate_ids: candidateIds }),
  // Explicit opt-in to send one Search Console query to Tavily. Never
  // inferred from import or watchlist approval; the checkbox is required.
  setExternalProcessingConsent: (clientId, promptId, { granted, disclosureVersion, acknowledged }) =>
    api.post(`/v2/watchlist/${clientId}/prompts/${promptId}/external-processing-consent`, {
      granted,
      disclosure_version: disclosureVersion,
      acknowledged,
    }),
};

export const searchDemandAPI = {
  get: (clientId) => api.get(`/v2/search-demand/${clientId}`),
};

export const ga4API = {
  get: (clientId) => api.get(`/v2/ga4/${clientId}`),
  refresh: (clientId) => api.post(`/v2/ga4/${clientId}/refresh`),
};

export const shopifyAPI = {
  get: (clientId) => api.get(`/v2/shopify/${clientId}`),
  refresh: (clientId) => api.post(`/v2/shopify/${clientId}/refresh`),
};

// Real customer-facing OAuth -- Shopify / Meta Ads / Google Search
// Console / Google Analytics / Google Ads. `start` returns a real
// provider authorize_url to navigate the browser to (never a token,
// never a client secret). `options`/`finalize` are the account/property
// selection step some providers need before the connection is complete.
export const oauthAPI = {
  start: (provider, clientId, extra = {}) => api.post(`/oauth/${provider}/start`, { client_id: clientId, ...extra }),
  options: (provider, workspaceId, pendingId) => api.get(`/oauth/${provider}/${workspaceId}/options`, { params: { pending_id: pendingId } }),
  finalize: (provider, workspaceId, pendingId, accountReference) =>
    api.post(`/oauth/${provider}/${workspaceId}/finalize`, { pending_id: pendingId, account_reference: accountReference }),
};

// Real customer-facing OAuth -- Instagram/Facebook/LinkedIn/TikTok/
// YouTube/X/Pinterest/Reddit/Threads. Martian's own abstraction
// (backend: social_connections.py) -- nothing here or on the page that
// consumes it ever mentions the infrastructure behind it.
export const socialConnectionsAPI = {
  list: (clientId) => api.get(`/social-connections/${clientId}`),
  connect: (clientId, platform) => api.post(`/social-connections/${clientId}/connect`, { platform }),
  healthCheck: (clientId, platform) => api.post(`/social-connections/${clientId}/${platform}/health-check`),
  disconnect: (clientId, platform) => api.post(`/social-connections/${clientId}/${platform}/disconnect`),
};

export const somPromptsAPI = {
  list: (clientId, includeInactive = false) =>
    api.get(`/v2/som-prompts/${clientId}`, includeInactive ? { params: { include_inactive: true } } : undefined),
  createCustom: (clientId, payload) => api.post(`/v2/som-prompts/${clientId}/custom`, payload),
  updateCustom: (clientId, promptId, payload) => api.patch(`/v2/som-prompts/${clientId}/${promptId}`, payload),
  activate: (clientId, promptId) => api.post(`/v2/som-prompts/${clientId}/${promptId}/activate`),
  deactivate: (clientId, promptId) => api.post(`/v2/som-prompts/${clientId}/${promptId}/deactivate`),
  runNow: (clientId, promptId) => api.post(`/v2/som-prompts/${clientId}/${promptId}/run`),
  history: (clientId, promptId) => api.get(`/v2/som-prompts/${clientId}/${promptId}/history`),
  customRuns: (clientId) => api.get(`/v2/som-prompts/${clientId}/runs`),
  usage: (clientId) => api.get(`/v2/som-prompts/${clientId}/usage`),
};

export const somHistoryAPI = {
  production: (clientId) => api.get(`/som/history/${clientId}`),
};

// Phase 1 (Founder-First UX) -- the Decision: one thread per real problem,
// a read-only aggregation of existing intelligence (backend decisions.py).
export const decisionsAPI = {
  list: (clientId) => api.get(`/decisions/${clientId}`),
  get: (clientId, decisionId) => api.get(`/decisions/${clientId}/${decisionId}`),
};

// Phase 1 -- the structured business model Martian itself uses (products,
// category, customer segments, markets), read-only.
export const businessProfileAPI = {
  get: (clientId) => api.get(`/business-profile/${clientId}`),
};


// AF-3.19 — real SoM dashboard (KPI cards with an honest UNAVAILABLE_KPI
// shape for anything server.py itself has no real data source for; see
// server.py's own get_som_dashboard() docstring).
//
// P2.3 — pass a clientId to scope every SoM figure (latest/previous
// score, trend, competitor comparison) to that one brand. Omit it and
// the response is the pre-P2.3 account-wide read (newest som_scores row
// across all the caller's clients) — kept for backward compatibility.
export const somDashboardAPI = {
  get: (clientId) => api.get('/som/dashboard', clientId ? { params: { client_id: clientId } } : undefined),
};

export default api;
